import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { getPlans, createOrder, markOrderPaid, formatEUR, resolveImageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, Lock, Trash2, ArrowLeft, ExternalLink, CheckCircle2, CreditCard, ShoppingBag } from "lucide-react";
import { CHECKOUT } from "@/constants/testIds";
import { trackBeginCheckout } from "@/lib/gtag";
import { PaypalBadge } from "@/components/PaypalBadge";

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const buildPayLink = (order) => {
  const link = order?.paypal_link;
  if (!link) return null;
  if (link.includes("paypal.me") || link.includes("paypalme")) {
    return `${link.replace(/\/$/, "")}/${Math.round(order.total)}EUR`;
  }
  return link;
};

export default function Checkout() {
  const navigate = useNavigate();
  const { plan, phone, total, selectPlan, removePhone, clearCart } = useCart();
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", notes: "" });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState(null);

  useEffect(() => {
    getPlans().then(setPlans).catch(() => {});
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (!plan && !phone) {
    return (
      <div data-testid={CHECKOUT.page} className="container-x py-24">
        <div className="mx-auto max-w-md text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-accent glow">
            <ShoppingBag className="h-7 w-7" />
          </span>
          <h1 className="mt-5 text-2xl font-semibold">Il tuo carrello è vuoto</h1>
          <p className="mt-3 text-muted-foreground">Scegli un abbonamento o un telefono per procedere all'ordine.</p>
          <Button asChild className="mt-6 rounded-xl glow-hover" data-testid={CHECKOUT.empty}>
            <Link to="/#prezzi">Vedi i piani</Link>
          </Button>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) return toast.error("Inserisci nome e cognome.");
    if (!emailRe.test(form.email)) return toast.error("Inserisci un'email valida.");
    if (form.phone.trim().length < 6) return toast.error("Inserisci un numero di telefono valido.");
    if (!consent) return toast.error("Devi accettare le condizioni d'uso lecito.");
    setSubmitting(true);
    try {
      const res = await createOrder({
        customer: form,
        plan_slug: plan ? plan.slug : null,
        phone_id: phone ? phone.id : null,
        consent,
      });
      setOrder(res);
      toast.success("Ordine creato! Procedi con il pagamento PayPal.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Errore nella creazione dell'ordine.");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmPaid = async () => {
    const oid = order.id;
    try {
      await markOrderPaid(oid);
    } catch (e) {}
    clearCart();
    navigate(`/success?order=${oid}`);
  };

  const payLink = buildPayLink(order);

  return (
    <div data-testid={CHECKOUT.page} className="container-x py-10 sm:py-14">
      <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft className="h-4 w-4" /> Torna alla home
      </button>
      <h1 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight">Completa il tuo ordine</h1>
      <p className="mt-2 text-muted-foreground">Inserisci i tuoi dati e procedi con il pagamento sicuro tramite PayPal.</p>

      <div className="mt-8 grid lg:grid-cols-5 gap-8">
        {/* Left */}
        <div className="lg:col-span-3">
          {!order ? (
            <Card className="p-6 sm:p-8 rounded-2xl border-border bg-card glow">
              <h2 className="text-lg font-semibold">I tuoi dati</h2>
              <form onSubmit={submit} className="mt-5 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="full_name">Nome e cognome</Label>
                  <Input id="full_name" data-testid={CHECKOUT.fullName} value={form.full_name} onChange={update("full_name")} placeholder="Mario Rossi" className="rounded-xl" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" data-testid={CHECKOUT.email} value={form.email} onChange={update("email")} placeholder="mario@email.it" className="rounded-xl" />
                    <p className="text-xs text-muted-foreground">Riceverai qui l'accesso al pannello cloud.</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="phone">Telefono</Label>
                    <Input id="phone" data-testid={CHECKOUT.phone} value={form.phone} onChange={update("phone")} placeholder="+39 333 1234567" className="rounded-xl" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="notes">Note (opzionale)</Label>
                  <Textarea id="notes" data-testid={CHECKOUT.notes} value={form.notes} onChange={update("notes")} placeholder="Eventuali richieste particolari..." rows={3} className="rounded-xl" />
                </div>

                <div className="flex items-start gap-3 rounded-xl border border-border bg-secondary/40 p-4">
                  <Checkbox id="consent" data-testid={CHECKOUT.consent} checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" />
                  <Label htmlFor="consent" className="text-xs text-muted-foreground font-normal leading-relaxed cursor-pointer">
                    Dichiaro che utilizzerò il servizio solo su dispositivi di mia proprietà o con il consenso esplicito dell'utilizzatore, nel pieno rispetto della legge. In caso di uso illecito, controllatelefoni.it collaborerà con le forze dell'ordine per individuare l'utilizzatore. Acquistando dichiaro di aver preso visione di tutte le normative vigenti in Italia e della pagina{" "}
                    <Link to="/uso-lecito" className="text-accent underline">Uso Lecito</Link>.
                  </Label>
                </div>

                <Button type="submit" disabled={submitting} data-testid={CHECKOUT.submit} className="w-full rounded-xl glow-hover h-12 text-base">
                  {submitting ? "Creazione ordine..." : (<><CreditCard className="h-4 w-4" /> Procedi al pagamento</>)}
                </Button>
              </form>
            </Card>
          ) : (
            <Card className="p-6 sm:p-8 rounded-2xl border-2 border-accent bg-card glow-strong">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-600 text-white"><CheckCircle2 className="h-6 w-6" /></span>
                <div>
                  <h2 className="text-lg font-semibold">Ordine creato</h2>
                  <p className="text-xs text-muted-foreground">Riferimento: #{order.id.slice(0, 8).toUpperCase()}</p>
                </div>
              </div>

              <div className="mt-6 rounded-xl border border-border bg-secondary/40 p-4">
                <p className="text-sm font-medium">Ultimo passo: completa il pagamento</p>
                <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                  Verrai indirizzato a PayPal per pagare in modo sicuro l'importo di <strong className="text-foreground">{formatEUR(order.total)}</strong>. Al termine, torna qui e conferma.
                </p>
              </div>

              {payLink ? (
                <a href={payLink} target="_blank" rel="noopener noreferrer" onClick={() => trackBeginCheckout({ value: order.total, items: order.items?.map((it) => ({ item_name: it.name, price: it.price, quantity: it.quantity || 1 })) })} className="mt-4 block">
                  <Button data-testid={CHECKOUT.paypal} className="w-full rounded-xl h-12 text-base glow-hover" style={{ backgroundColor: "#0070ba", color: "#fff" }}>
                    <ExternalLink className="h-4 w-4" /> Paga con PayPal ({formatEUR(order.total)})
                  </Button>
                </a>
              ) : (
                <div className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-foreground">
                  Il pagamento online non è ancora configurato. Ti contatteremo via email per completare il pagamento.
                </div>
              )}

              <Button onClick={confirmPaid} variant="outline" data-testid={CHECKOUT.markPaid} className="mt-3 w-full rounded-xl h-11">
                Ho effettuato il pagamento
              </Button>
              <p className="mt-3 text-xs text-muted-foreground text-center">
                Il pagamento verrà verificato manualmente. Riceverai l'accesso via email entro poche ore.
              </p>
            </Card>
          )}
        </div>

        {/* Right - summary */}
        <div className="lg:col-span-2">
          <Card data-testid={CHECKOUT.summary} className="p-6 rounded-2xl border-border bg-card glow lg:sticky lg:top-24">
            <h2 className="text-lg font-semibold">Riepilogo ordine</h2>
            <Separator className="my-4" />

            <div className="space-y-4">
              {plan && (
                <div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">Abbonamento {plan.name}</p>
                    <p className="text-sm font-semibold">{formatEUR(plan.price)}</p>
                  </div>
                  {!order && plans.length > 0 && (
                    <div className="mt-2">
                      <Select value={plan.slug} onValueChange={(slug) => { const np = plans.find((x) => x.slug === slug); if (np) selectPlan(np); }}>
                        <SelectTrigger data-testid={CHECKOUT.planSelect} className="rounded-xl h-9 text-xs">
                          <SelectValue placeholder="Cambia durata" />
                        </SelectTrigger>
                        <SelectContent>
                          {plans.map((pl) => (
                            <SelectItem key={pl.slug} value={pl.slug}>{pl.name} — {formatEUR(pl.price)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              {phone && (
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <img src={resolveImageUrl(phone.image)} alt={phone.name} className="h-12 w-12 rounded-lg object-cover border border-border" />
                    <div>
                      <p className="text-sm font-medium leading-tight">{phone.brand} {phone.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {phone.subscription_included ? `Servizio + ${phone.subscription_included} di pannello inclusi` : "Servizio preinstallato"}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold">{formatEUR(phone.price)}</p>
                    {!order && (
                      <button onClick={removePhone} data-testid={CHECKOUT.removePhone} className="text-xs text-destructive inline-flex items-center gap-1 hover:underline">
                        <Trash2 className="h-3 w-3" /> Rimuovi
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            <Separator className="my-4" />
            <div className="flex items-center justify-between">
              <p className="text-base font-semibold">Totale</p>
              <p className="font-display text-2xl font-semibold">{formatEUR(total)}</p>
            </div>

            <div className="mt-5 space-y-2 text-xs text-muted-foreground">
              <p className="flex items-center gap-2"><Lock className="h-3.5 w-3.5 text-accent" /> Pagamento sicuro tramite PayPal</p>
              <p className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-accent" /> Accesso al pannello inviato via email</p>
            </div>
            <PaypalBadge testId="paypal-badge-checkout" className="mt-4 w-full justify-center" />
          </Card>
        </div>
      </div>
    </div>
  );
}
